import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminMollieWebhookEvents } from "@/lib/admin-pos-payments.functions";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/webhook-events")({
  head: () => ({
    meta: [
      { title: "Admin — Mollie webhook events" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WebhookEventsPage,
});

function fmt(ts: string | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("nl-NL", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

function money(cents: number | null) {
  if (cents == null) return "—";
  return `€ ${(cents / 100).toFixed(2)}`;
}

function outcomeBadge(outcome: string | null, processed_at: string | null) {
  if (!processed_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        <Clock className="h-3 w-3" /> in behandeling
      </span>
    );
  }
  const isError =
    outcome &&
    /(error|fail|invalid|unauthorized|missing|reject)/i.test(outcome);
  if (isError) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
        <AlertTriangle className="h-3 w-3" /> {outcome}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
      <CheckCircle2 className="h-3 w-3" /> {outcome ?? "ok"}
    </span>
  );
}

function WebhookEventsPage() {
  const fetchEvents = useServerFn(adminMollieWebhookEvents);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "mollie-webhook-events"],
    queryFn: () => fetchEvents(),
    refetchInterval: 15_000,
  });

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Mollie webhook events</h1>
          <p className="text-sm text-muted-foreground">
            Idempotente log van inkomende Mollie webhook-aanroepen (POS & online).
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          disabled={isFetching}
        >
          {isFetching ? "Verversen…" : "Verversen"}
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error && (
        <p className="text-sm text-red-600">
          Kon events niet laden: {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Ontvangen</th>
                <th className="px-3 py-2">Mollie payment ID</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Methode</th>
                <th className="px-3 py-2 text-right">Bedrag</th>
                <th className="px-3 py-2">Order ID</th>
                <th className="px-3 py-2">Verwerkt op</th>
                <th className="px-3 py-2">Uitkomst</th>
                <th className="px-3 py-2">Opmerking / fout</th>
              </tr>
            </thead>
            <tbody>
              {data.events.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    Nog geen webhook-events ontvangen.
                  </td>
                </tr>
              )}
              {data.events.map((ev: any) => (
                <tr key={ev.id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fmt(ev.received_at)}
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-xs"
                    title={ev.mollie_payment_id}
                  >
                    {ev.mollie_payment_id}
                  </td>
                  <td className="px-3 py-2">{ev.status}</td>
                  <td className="px-3 py-2">{ev.payment_method ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {money(ev.amount_cents)}
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-xs"
                    title={ev.order_id ?? ""}
                  >
                    {ev.order_id ? `${String(ev.order_id).slice(0, 8)}…` : "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fmt(ev.processed_at)}
                  </td>
                  <td className="px-3 py-2">
                    {outcomeBadge(ev.outcome, ev.processed_at)}
                  </td>
                  <td
                    className="max-w-md px-3 py-2 text-xs text-muted-foreground"
                    title={ev.note ?? ""}
                  >
                    {ev.note ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
